import React, { useState, useRef, useCallback, useContext, useEffect } from 'react';
import {
  Box, Typography, Button, TextField,
  CircularProgress, Paper, IconButton, Divider,
  DialogContent, DialogTitle, useMediaQuery, useTheme, Slide, Chip
} from '@mui/material';
import Webcam from 'react-webcam';
import Dialog from "./ManagedModal";

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MyLocationIcon   from '@mui/icons-material/MyLocation';
import CloseIcon        from '@mui/icons-material/Close';
import FaceIcon         from '@mui/icons-material/Face';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VideocamOffIcon  from '@mui/icons-material/VideocamOff';
import RefreshIcon      from '@mui/icons-material/Refresh';
import VisibilityIcon   from '@mui/icons-material/Visibility';

import AuthContext      from './auth/Context';
import LookupFieldControl from './LookupFieldControl';

const customerFieldConfig = {
  label: "Chọn Khách Hàng (Tùy chọn)",
  type: "Autocomplete",
  api: "customer",
  apiMapping: { value: "ma_kh", label: "ten_kh" },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const dataURLtoFile = (dataurl, filename) => {
  let arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
  while (n--) { u8arr[n] = bstr.charCodeAt(n); }
  return new File([u8arr], filename, { type: mime });
};

const getCurrentPositionFallback = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) { reject(new Error("Trình duyệt không hỗ trợ định vị.")); return; }
  navigator.geolocation.getCurrentPosition(
    (p) => resolve(p.coords),
    (e) => {
      if (e.code === e.PERMISSION_DENIED) reject(new Error("Bạn chưa cấp quyền vị trí!"));
      else if (e.code === e.POSITION_UNAVAILABLE) reject(new Error("Vị trí không khả dụng."));
      else if (e.code === e.TIMEOUT) reject(new Error("Quá thời gian lấy vị trí."));
      else reject(new Error("Lỗi GPS không xác định."));
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );
});

// ─── Camera error helpers ──────────────────────────────────────────────────────
const CAMERA_ERROR = {
  PERMISSION_DENIED:    'permission_denied',
  PERMISSION_DISMISSED: 'permission_dismissed',
  DEVICE_NOT_FOUND:     'device_not_found',
  DEVICE_IN_USE:        'device_in_use',
  UNKNOWN:              'unknown',
};
const parseCameraError = (err) => {
  const name = err?.name || '';
  const msg  = (err?.message || '').toLowerCase();
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError')
    return (msg.includes('dismissed') || msg.includes('ignored'))
      ? CAMERA_ERROR.PERMISSION_DISMISSED : CAMERA_ERROR.PERMISSION_DENIED;
  if (name === 'NotFoundError'    || name === 'DevicesNotFoundError') return CAMERA_ERROR.DEVICE_NOT_FOUND;
  if (name === 'NotReadableError' || name === 'TrackStartError')      return CAMERA_ERROR.DEVICE_IN_USE;
  return CAMERA_ERROR.UNKNOWN;
};
const getPermissionGuide = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return { icon: '🍎', steps: ['Vào Cài đặt → Safari / Chrome', 'Camera → Cho phép', 'Quay lại → Thử lại'] };
  if (/Android/.test(ua))     return { icon: '🤖', steps: ['Nhấn 🔒 trên thanh địa chỉ', 'Quyền → Camera → Cho phép', 'Nhấn Thử lại'] };
  return { icon: '💻', steps: ['Nhấn 🔒 hoặc 📷 trên thanh địa chỉ', 'Camera → "Luôn cho phép"', 'Nhấn Thử lại'] };
};
const CAMERA_ERROR_UI = {
  [CAMERA_ERROR.PERMISSION_DENIED]:    { title: 'Quyền camera bị từ chối',  desc: 'Hãy mở lại quyền theo hướng dẫn rồi nhấn Thử lại.', showGuide: true  },
  [CAMERA_ERROR.PERMISSION_DISMISSED]: { title: 'Chưa cấp quyền camera',    desc: 'Nhấn Thử lại để hiện lại yêu cầu quyền.',            showGuide: false },
  [CAMERA_ERROR.DEVICE_NOT_FOUND]:     { title: 'Không tìm thấy camera',    desc: 'Thiết bị không có camera hoặc chưa kết nối.',         showGuide: false },
  [CAMERA_ERROR.DEVICE_IN_USE]:        { title: 'Camera đang được sử dụng', desc: 'Camera bị chiếm bởi app khác (Zoom, Meet…).',         showGuide: false },
  [CAMERA_ERROR.UNKNOWN]:              { title: 'Không thể mở camera',      desc: 'Đã xảy ra lỗi không xác định.',                      showGuide: false },
};

// ─── Face status ───────────────────────────────────────────────────────────────
const FACE_STATUS = { LOADING: 'loading', NO_FACE: 'no_face', MULTI_FACE: 'multi_face', OK: 'ok', ERROR: 'error' };
const FACE_STATUS_CONFIG = {
  [FACE_STATUS.LOADING]:    { color: '#5c6bc0', bg: 'rgba(92,107,192,0.18)',  label: 'Đang nạp AI…',           boxColor: '#90caf9' },
  [FACE_STATUS.NO_FACE]:    { color: '#e53935', bg: 'rgba(229,57,53,0.15)',   label: 'Không thấy khuôn mặt',   boxColor: '#ef5350' },
  [FACE_STATUS.MULTI_FACE]: { color: '#f57c00', bg: 'rgba(245,124,0,0.15)',   label: 'Nhiều hơn 1 khuôn mặt',  boxColor: '#ffa726' },
  [FACE_STATUS.OK]:         { color: '#2e7d32', bg: 'rgba(46,125,50,0.18)',   label: 'Khuôn mặt hợp lệ ✓',    boxColor: '#66bb6a' },
  [FACE_STATUS.ERROR]:      { color: '#6a1b9a', bg: 'rgba(106,27,154,0.15)', label: 'Lỗi AI',                 boxColor: '#ab47bc' },
};

// ─── Liveness detection ────────────────────────────────────────────────────────
// Strategy: Eye Aspect Ratio (EAR) blink detection via FaceLandmarker 478-pt mesh
// A static photo / screen replay cannot blink → blink = proof of live person
const LIVENESS = { IDLE: 'idle', WATCHING: 'watching', LIVE: 'live' };
const LIVENESS_CONFIG = {
  [LIVENESS.IDLE]:     { color: '#78909c', bg: 'rgba(120,144,156,0.15)', label: '—' },
  [LIVENESS.WATCHING]: { color: '#f57c00', bg: 'rgba(245,124,0,0.15)',   label: 'Chớp mắt để xác nhận' },
  [LIVENESS.LIVE]:     { color: '#2e7d32', bg: 'rgba(46,125,50,0.18)',   label: '✓ Đã xác nhận sống động' },
};

// MediaPipe 478-pt face mesh eye landmark indices
// Order: [P1=outer_corner, P2=upper_outer, P3=upper_inner, P4=inner_corner, P5=lower_inner, P6=lower_outer]
const L_EYE_IDX = [362, 385, 387, 263, 373, 380];
const R_EYE_IDX = [33,  160, 158, 133, 153, 144];

// EAR = (||P2-P6|| + ||P3-P5||) / (2 * ||P1-P4||)
// Open eye ≈ 0.25–0.35 | Closed eye ≈ 0.10–0.18
const lmDist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const getEAR  = (lms, idx) => {
  const [p1, p2, p3, p4, p5, p6] = idx.map(i => lms[i]);
  return (lmDist(p2, p6) + lmDist(p3, p5)) / (2 * lmDist(p1, p4));
};

const EAR_CLOSED      = 0.18;   // EAR drops below this → eye closing
const EAR_OPEN        = 0.24;   // EAR rises above this (after close) → blink complete
const BLINK_WINDOW_MS = 8000;   // user has 8s to blink before timer resets

// FaceLandmarker model (~12 MB, provides 478-pt landmarks for EAR + face position)
const FACE_LANDMARKER_MODEL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

// Bounding box from normalized [0,1] landmarks → canvas pixel coords
const landmarksBoundingBox = (landmarks, cw, ch, pad = 0.05) => {
  let minX = 1, minY = 1, maxX = 0, maxY = 0;
  for (const lm of landmarks) {
    if (lm.x < minX) minX = lm.x; if (lm.x > maxX) maxX = lm.x;
    if (lm.y < minY) minY = lm.y; if (lm.y > maxY) maxY = lm.y;
  }
  return {
    x: Math.max(0, minX - pad) * cw,
    y: Math.max(0, minY - pad) * ch,
    w: Math.min(1, maxX - minX + pad * 2) * cw,
    h: Math.min(1, maxY - minY + pad * 2) * ch,
  };
};

// ─── HUD debug bar ─────────────────────────────────────────────────────────────
const HudBar = React.memo(({ faceStatus, faceDetector, aiLoadingError, imageSrc, liveness }) => {
  const dotRef      = useRef(null);
  const fpsRef      = useRef(null);
  const frameRef    = useRef(null);
  const earRef      = useRef(null);
  const countdownRef= useRef(null);
  const liveDotRef  = useRef(null);

  useEffect(() => {
    window.__hudSetters = {
      setActive:    (v) => {
        if (dotRef.current) dotRef.current.style.background = v ? '#69f0ae' : '#ef5350';
      },
      setFps:       (v) => { if (fpsRef.current)       fpsRef.current.textContent    = v; },
      setFrame:     (v) => { if (frameRef.current)      frameRef.current.textContent  = v; },
      setEAR:       (v) => { if (earRef.current)        earRef.current.textContent    = v; },
      setCountdown: (v) => { if (countdownRef.current)  countdownRef.current.textContent = v; },
      setLiveDot:   (v) => {
        if (liveDotRef.current) {
          liveDotRef.current.style.background = v === 'live' ? '#69f0ae' : v === 'watching' ? '#ffa726' : '#546e7a';
          liveDotRef.current.style.boxShadow  = v === 'live' ? '0 0 6px #69f0ae' : 'none';
        }
      },
    };
  }, []);

  const cfg       = FACE_STATUS_CONFIG[faceStatus] || FACE_STATUS_CONFIG[FACE_STATUS.LOADING];
  const liveCfg   = LIVENESS_CONFIG[liveness] || LIVENESS_CONFIG[LIVENESS.IDLE];
  const isRunning = faceDetector && !aiLoadingError && !imageSrc;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      px: 1.5, py: 0.75, bgcolor: '#0d1117',
      borderRadius: '0 0 10px 10px',
      fontFamily: '"Roboto Mono", monospace', fontSize: '0.67rem', userSelect: 'none',
    }}>
      {/* Engine status dot */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box ref={dotRef} sx={{
          width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
          bgcolor: isRunning ? '#69f0ae' : '#ef5350',
          boxShadow: isRunning ? '0 0 5px #69f0ae' : 'none',
          animation: isRunning ? 'hpulse 1.2s ease-in-out infinite' : 'none',
          '@keyframes hpulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
        }} />
        <span style={{ color: isRunning ? '#69f0ae' : '#ef5350', fontWeight: 700 }}>
          {isRunning ? 'AI LIVE' : faceDetector ? 'PAUSED' : 'LOADING'}
        </span>
      </Box>

      <span style={{ color: '#30363d' }}>│</span>

      {/* Face status */}
      <Box sx={{ px: 0.7, py: 0.15, borderRadius: 0.75, bgcolor: cfg.bg, border: `1px solid ${cfg.color}44`, color: cfg.color, fontWeight: 600, transition: 'all 0.2s' }}>
        {cfg.label}
      </Box>

      <span style={{ color: '#30363d' }}>│</span>

      {/* Liveness status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box ref={liveDotRef} sx={{
          width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
          bgcolor: liveness === LIVENESS.LIVE ? '#69f0ae' : liveness === LIVENESS.WATCHING ? '#ffa726' : '#546e7a',
          boxShadow: liveness === LIVENESS.LIVE ? '0 0 5px #69f0ae' : 'none',
        }} />
        <Box sx={{ px: 0.7, py: 0.15, borderRadius: 0.75, bgcolor: liveCfg.bg, border: `1px solid ${liveCfg.color}44`, color: liveCfg.color, fontWeight: 600, transition: 'all 0.2s' }}>
          {liveness === LIVENESS.WATCHING
            ? <><span>Chớp mắt… </span><span ref={countdownRef} style={{ color: '#ffa726', fontWeight: 800 }}>8</span><span>s</span></>
            : liveCfg.label
          }
        </Box>
      </Box>

      <span style={{ color: '#30363d' }}>│</span>

      {/* FPS */}
      <Box sx={{ display: 'flex', gap: 0.5, color: '#8b949e' }}>
        <span style={{ color: '#484f58' }}>FPS</span>
        <span ref={fpsRef} style={{ color: '#c9d1d9', fontWeight: 700, minWidth: 22, display: 'inline-block' }}>—</span>
      </Box>

      {/* Frame */}
      <Box sx={{ display: 'flex', gap: 0.5, color: '#8b949e' }}>
        <span style={{ color: '#484f58' }}>F</span>
        <span ref={frameRef} style={{ color: '#c9d1d9', fontWeight: 600, minWidth: 28, display: 'inline-block' }}>—</span>
      </Box>

      {/* EAR */}
      <Box sx={{ display: 'flex', gap: 0.5, color: '#8b949e' }}>
        <span style={{ color: '#484f58' }}>EAR</span>
        <span ref={earRef} style={{ color: '#c9d1d9', fontWeight: 600, minWidth: 34, display: 'inline-block' }}>—</span>
      </Box>

      {/*<Box sx={{ ml: 'auto', color: '#30363d', fontSize: '0.58rem' }}>FaceLandmarker</Box>*/}
    </Box>
  );
});

// ─── Main component ─────────────────────────────────────────────────────────────
export default function CheckInDialog({ open, onClose, utils }) {
  const context    = useContext(AuthContext);
  const theme      = useTheme();
  const isMobile   = useMediaQuery(theme.breakpoints.down('sm'));
  const userToken  = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  const webcamRef   = useRef(null);
  const canvasRef   = useRef(null);
  const rafRef      = useRef(null);
  const detectorRef = useRef(null);  // FaceLandmarker instance

  // rAF perf counters (no re-renders)
  const frameCountRef   = useRef(0);
  const lastFpsTimeRef  = useRef(performance.now());
  const fpsCountRef     = useRef(0);

  // Liveness refs (live values used inside rAF loop)
  const livenessRef       = useRef(LIVENESS.IDLE);
  const eyeWasClosedRef   = useRef(false);
  const livenessStartRef  = useRef(null);
  const prevCountdownRef  = useRef(null);
  const livenessOkAtCapture = useRef(false);  // flag set at capture time

  const [imageSrc,       setImageSrc]       = useState(null);
  const [note,           setNote]           = useState('');
  const [customerId,     setCustomerId]     = useState('');
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [loadingStep,    setLoadingStep]    = useState('');
  const [cameraError,    setCameraError]    = useState(null);
  const [webcamKey,      setWebcamKey]      = useState(0);
  const [faceDetector,   setFaceDetector]   = useState(null);
  const [aiLoadingError, setAiLoadingError] = useState('');
  const [faceStatus,     setFaceStatus]     = useState(FACE_STATUS.LOADING);
  const [liveness,       setLiveness]       = useState(LIVENESS.IDLE);
  const [needsManualReview, setNeedsManualReview] = useState(false); // true → AI không xác thực được, chờ HR hậu kiểm

  const videoConstraints = { width: 480, height: 480, facingMode: 'user' };

  // ── Load FaceLandmarker ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    let isMounted = true;
    setFaceStatus(FACE_STATUS.LOADING);
    setAiLoadingError('');
    setLiveness(LIVENESS.IDLE);
    livenessRef.current = LIVENESS.IDLE;

    (async () => {
      try {
        const vision = await import(/* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.8/vision_bundle.mjs');
        const { FaceLandmarker, FilesetResolver } = vision;
        const fsr = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.8/wasm'
        );
        const landmarker = await FaceLandmarker.createFromOptions(fsr, {
          baseOptions: { modelAssetPath: FACE_LANDMARKER_MODEL, delegate: 'GPU' },
          runningMode: 'VIDEO',   // VIDEO mode: temporal smoothing, requires detectForVideo()
          numFaces: 2,            // detect up to 2 to catch multi-face
          minFaceDetectionConfidence: 0.5,
          minFacePresenceScore:       0.5,
          minTrackingConfidence:      0.5,
        });
        if (isMounted) {
          detectorRef.current = landmarker;
          setFaceDetector(landmarker);
          setFaceStatus(FACE_STATUS.NO_FACE);
        }
      } catch (err) {
        if (isMounted) { setAiLoadingError(err.message); setFaceStatus(FACE_STATUS.ERROR); }
      }
    })();
    return () => { isMounted = false; };
  }, [open]);

  // ── Reset liveness ───────────────────────────────────────────────────────────
  const resetLiveness = useCallback(() => {
    livenessRef.current     = LIVENESS.IDLE;
    eyeWasClosedRef.current = false;
    livenessStartRef.current = null;
    prevCountdownRef.current = null;
    setLiveness(LIVENESS.IDLE);
    window.__hudSetters?.setLiveDot?.('idle');
    window.__hudSetters?.setEAR?.('—');
    window.__hudSetters?.setCountdown?.('—');
  }, []);

  // ── Draw overlay from FaceLandmarker results ─────────────────────────────────
  const drawOverlay = useCallback((canvas, allFaceLandmarks, currentLiveness) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;

    // Mirror the canvas context to match the CSS-mirrored <video>
    ctx.save();
    ctx.scale(-1, 1);
    ctx.translate(-W, 0);
    ctx.clearRect(0, 0, W, H);

    for (const landmarks of allFaceLandmarks) {
      const { x, y, w, h } = landmarksBoundingBox(landmarks, W, H);
      const r = 12;

      // Box color: green if live, orange if watching, red if multi
      const isLive      = currentLiveness === LIVENESS.LIVE && allFaceLandmarks.length === 1;
      const isWatching  = currentLiveness === LIVENESS.WATCHING;
      const boxColor    = allFaceLandmarks.length > 1 ? '#ffa726'
        : isLive    ? '#66bb6a'
        : isWatching ? '#ffa726'
        : '#ef5350';

      // Rounded bounding box
      ctx.shadowColor = boxColor; ctx.shadowBlur = 18;
      ctx.strokeStyle = boxColor; ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(x+r,y); ctx.lineTo(x+w-r,y); ctx.quadraticCurveTo(x+w,y,x+w,y+r);
      ctx.lineTo(x+w,y+h-r); ctx.quadraticCurveTo(x+w,y+h,x+w-r,y+h);
      ctx.lineTo(x+r,y+h); ctx.quadraticCurveTo(x,y+h,x,y+h-r);
      ctx.lineTo(x,y+r); ctx.quadraticCurveTo(x,y,x+r,y);
      ctx.closePath(); ctx.stroke();
      ctx.shadowBlur = 0;

      // Corner accents
      const cL = Math.min(w, h) * 0.18;
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 3;
      ctx.shadowColor = boxColor; ctx.shadowBlur = 10;
      [[x,y,x+cL,y,x,y+cL],[x+w,y,x+w-cL,y,x+w,y+cL],
       [x,y+h,x+cL,y+h,x,y+h-cL],[x+w,y+h,x+w-cL,y+h,x+w,y+h-cL]]
        .forEach(([ax,ay,bx,by,cx,cy]) => {
          ctx.beginPath(); ctx.moveTo(bx,by); ctx.lineTo(ax,ay); ctx.lineTo(cx,cy); ctx.stroke();
        });
      ctx.shadowBlur = 0;

      // Eye landmark dots (small) — visualize EAR points
      if (allFaceLandmarks.length === 1) {
        const eyeColor = isLive ? '#a5d6a7' : '#ffe082';
        ctx.fillStyle = eyeColor;
        [...L_EYE_IDX, ...R_EYE_IDX].forEach(idx => {
          const lm = landmarks[idx];
          ctx.beginPath();
          ctx.arc(lm.x * W, lm.y * H, 2, 0, Math.PI * 2);
          ctx.fill();
        });
      }

      // Label: "LIVE ✓" or eye icon when watching
      if (isLive) {
        ctx.font = 'bold 13px monospace';
        ctx.fillStyle = '#66bb6a';
        ctx.shadowColor = '#66bb6a'; ctx.shadowBlur = 8;
        ctx.fillText('LIVE ✓', x + 4, y - 7);
        ctx.shadowBlur = 0;
      } else if (isWatching) {
        ctx.font = 'bold 12px monospace';
        ctx.fillStyle = '#ffa726';
        ctx.fillText('👁 Chớp mắt…', x + 4, y - 7);
      }
    }

    ctx.restore();
  }, []);

  // ── rAF loop with EAR blink detection ───────────────────────────────────────
  useEffect(() => {
    if (imageSrc || !faceDetector || cameraError || aiLoadingError) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.__hudSetters?.setActive?.(false);
      return;
    }

    const loop = () => {
      const videoEl = webcamRef.current?.video;
      const canvas  = canvasRef.current;

      if (videoEl && videoEl.readyState >= 2 && detectorRef.current) {
        try {
          // VIDEO mode requires timestamp in ms
          const ts = performance.now();
          const results = detectorRef.current.detectForVideo(videoEl, ts);
          const allLandmarks = results.faceLandmarks || [];

          // ── Sync canvas to video resolution ─────────────────────────────
          const vw = videoEl.videoWidth || 480, vh = videoEl.videoHeight || 480;
          if (canvas.width !== vw || canvas.height !== vh) { canvas.width = vw; canvas.height = vh; }

          // ── Face status ──────────────────────────────────────────────────
          const faceCount = allLandmarks.length;
          const newFaceStatus = faceCount === 0 ? FACE_STATUS.NO_FACE
            : faceCount > 1 ? FACE_STATUS.MULTI_FACE : FACE_STATUS.OK;
          setFaceStatus(prev => prev !== newFaceStatus ? newFaceStatus : prev);

          // ── Liveness: EAR blink detection ───────────────────────────────
          if (faceCount === 1) {
            const lms = allLandmarks[0];
            const avgEAR = (getEAR(lms, L_EYE_IDX) + getEAR(lms, R_EYE_IDX)) / 2;
            window.__hudSetters?.setEAR?.(avgEAR.toFixed(3));

            const curLiveness = livenessRef.current;

            if (curLiveness !== LIVENESS.LIVE) {
              // Start timer when face first appears
              if (curLiveness === LIVENESS.IDLE) {
                livenessRef.current  = LIVENESS.WATCHING;
                livenessStartRef.current = performance.now();
                eyeWasClosedRef.current  = false;
                setLiveness(LIVENESS.WATCHING);
                window.__hudSetters?.setLiveDot?.('watching');
              }

              // Countdown
              const elapsed   = performance.now() - livenessStartRef.current;
              const remaining = Math.max(0, Math.ceil((BLINK_WINDOW_MS - elapsed) / 1000));
              if (remaining !== prevCountdownRef.current) {
                prevCountdownRef.current = remaining;
                window.__hudSetters?.setCountdown?.(remaining);
              }

              // Timeout → reset (give another chance instead of failing)
              if (elapsed >= BLINK_WINDOW_MS) {
                livenessRef.current      = LIVENESS.IDLE;
                eyeWasClosedRef.current  = false;
                livenessStartRef.current = null;
                setLiveness(LIVENESS.IDLE);
                window.__hudSetters?.setLiveDot?.('idle');

              // Blink state machine: eye closed → eye opens = 1 blink
              } else if (avgEAR < EAR_CLOSED && !eyeWasClosedRef.current) {
                eyeWasClosedRef.current = true;                  // eye closing

              } else if (avgEAR > EAR_OPEN && eyeWasClosedRef.current) {
                // ✓ Blink confirmed!
                livenessRef.current = LIVENESS.LIVE;
                setLiveness(LIVENESS.LIVE);
                window.__hudSetters?.setLiveDot?.('live');
                window.__hudSetters?.setCountdown?.('✓');
              }
            }
          } else {
            // Face lost or multiple faces → reset liveness silently
            if (livenessRef.current !== LIVENESS.IDLE) {
              livenessRef.current      = LIVENESS.IDLE;
              eyeWasClosedRef.current  = false;
              livenessStartRef.current = null;
              setLiveness(LIVENESS.IDLE);
              window.__hudSetters?.setLiveDot?.('idle');
              window.__hudSetters?.setEAR?.('—');
            }
          }

          // ── Draw overlay ─────────────────────────────────────────────────
          drawOverlay(canvas, allLandmarks, livenessRef.current);

          // ── HUD perf counters ────────────────────────────────────────────
          frameCountRef.current++;
          fpsCountRef.current++;
          const now     = performance.now();
          const elapsed = now - lastFpsTimeRef.current;
          if (elapsed >= 1000) {
            window.__hudSetters?.setFps?.(Math.round((fpsCountRef.current * 1000) / elapsed));
            window.__hudSetters?.setFrame?.(frameCountRef.current);
            window.__hudSetters?.setActive?.(true);
            fpsCountRef.current    = 0;
            lastFpsTimeRef.current = now;
          }

        } catch (e) { /* silent — avoid crashing the loop */ }
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [faceDetector, imageSrc, cameraError, aiLoadingError, drawOverlay]);

  // Stop loop & clear canvas when photo is captured
  useEffect(() => {
    if (imageSrc) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
  }, [imageSrc]);

  // ── Reason message when AI cannot verify a live face ─────────────────────────
  const getUnverifiedReasonMessage = () => {
    if (aiLoadingError)
      return "Hệ thống AI nhận diện khuôn mặt hiện không khả dụng.";
    if (!faceDetector)
      return "Hệ thống AI đang khởi tạo, chưa thể xác thực khuôn mặt.";
    if (faceStatus === FACE_STATUS.MULTI_FACE)
      return "Hệ thống phát hiện nhiều hơn 1 khuôn mặt trong khung hình.";
    if (faceStatus !== FACE_STATUS.OK)
      return "Hệ thống không nhận diện được khuôn mặt của anh/chị.";
    return "Hệ thống chưa xác thực được khuôn mặt sống động (chưa phát hiện chớp mắt).";
  };

  const handleCameraError  = useCallback((err) => { setCameraError(parseCameraError(err)); }, []);
  const handleRetryCamera  = useCallback(() => { setCameraError(null); setWebcamKey(k => k + 1); }, []);

  const handleClose = () => {
    if (isSubmitting) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setImageSrc(null); setNote(''); setCustomerId('');
    setCameraError(null);
    livenessOkAtCapture.current = false;
    setNeedsManualReview(false);
    resetLiveness();
    setFaceStatus(FACE_STATUS.LOADING);
    if (onClose) onClose();
  };

  // ── Capture the current frame + run the full check-in flow ───────────────────
  const performCheckIn = async (verified) => {
    if (!webcamRef.current) { context.alert?.("Không thể truy cập camera!", null, "red"); return; }
    const shot = webcamRef.current.getScreenshot({
      width: 480,
      height: 480,
      quality: 0.35 // Mức nén 35% cho dung lượng cực thấp nhưng AI và nhân sự vẫn nhìn rõ
    });
    
    if (!shot) { context.alert?.("Không thể chụp ảnh từ camera. Vui lòng thử lại.", null, "red"); return; }

    livenessOkAtCapture.current = verified;
    setNeedsManualReview(!verified);
    setImageSrc(shot);   // freeze the frame as visual feedback while submitting
    setIsSubmitting(true);

    try {
      setLoadingStep('Đang lấy vị trí GPS…');
      const coords = utils?.getCurrentPosition
        ? await utils.getCurrentPosition()
        : await getCurrentPositionFallback();

      setLoadingStep('Đang tải ảnh lên…');
      const uploadRes = await context.apis.uploadSingleFile(
        userToken, dataURLtoFile(shot, `checkin_${Date.now()}.jpg`)
      );
      if (!uploadRes || uploadRes.error) throw new Error("Lỗi tải ảnh: " + (uploadRes?.error || ""));
      const hinhUrl = `/api/${context.appInfo._id}/file/download/${uploadRes._id}`;

      setLoadingStep('Đang lưu dữ liệu…');
      const deviceId = typeof context.getDeviceId === 'function' ? context.getDeviceId() : 'UNKNOWN_DEVICE';
      const finalNote = !verified
        ? `[CẦN HẬU KIỂM - AI KHÔNG XÁC THỰC ĐƯỢC KHUÔN MẶT] ${note.trim()}`.trim()
        : note.trim();
      const res = await context.apis.asyncPostList(userToken, "checkin", {
        trang_thai: "1", device_user_id: context.userInfo.email,
        record_time: new Date(), ma_kh: customerId || null,
        note: finalNote, location: { latitude: coords.latitude, longitude: coords.longitude },
        device_id: deviceId, listinfo_code: "checkin_chamcong", hinh: hinhUrl,
        ai_verified: verified,
      });
      if (res && !res.error) {
        context.alert?.(
          verified ? "Chấm công thành công!" : "Đã chấm công! Lưu ý: bộ phận nhân sự sẽ hậu kiểm ảnh của anh/chị."
        );
        handleClose();
      } else throw new Error(res?.error || "Lỗi lưu dữ liệu!");
    } catch (err) {
      context.alert?.(err.message || "Lỗi hệ thống!", null, "red");
      // Quay lại camera live để người dùng thử lại
      setImageSrc(null);
      setNeedsManualReview(false);
      livenessOkAtCapture.current = false;
    } finally {
      setIsSubmitting(false);
      setLoadingStep('');
    }
  };

  // ── Single entry point: nút "Xác nhận chấm công" tự chụp ảnh rồi nộp luôn ────
  const handleCheckIn = () => {
    if (cameraError || isSubmitting) return;

    const isVerified = faceStatus === FACE_STATUS.OK && liveness === LIVENESS.LIVE;
    if (isVerified) { performCheckIn(true); return; }

    // Chưa xác thực được khuôn mặt — hỏi xác nhận trước khi vẫn cho phép chấm công
    const reason = getUnverifiedReasonMessage();
    context.alert?.(
      `${reason} Bộ phận nhân sự sẽ hậu kiểm sau. Bạn có muốn tiếp tục chấm công không?`,
      (ok) => { if (ok) performCheckIn(false); }
    );
  };

  // ── Camera error sub-components ──────────────────────────────────────────────
  const CameraErrorIconStrip = () => (
    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:1, color:'white', py:3 }}>
      <VideocamOffIcon sx={{ fontSize: 40, color: '#ef9a9a' }} />
      <Typography variant="caption" color="#ef9a9a" fontWeight={700}>
        {(CAMERA_ERROR_UI[cameraError] || CAMERA_ERROR_UI[CAMERA_ERROR.UNKNOWN]).title}
      </Typography>
    </Box>
  );

  const CameraErrorDetails = () => {
    const ui    = CAMERA_ERROR_UI[cameraError] || CAMERA_ERROR_UI[CAMERA_ERROR.UNKNOWN];
    const guide = ui.showGuide ? getPermissionGuide() : null;
    return (
      <Box sx={{ p:2, pt:1.5, display:'flex', flexDirection:'column', alignItems:'center', gap:1.5, textAlign:'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 340 }}>{ui.desc}</Typography>
        {guide && (
          <Box sx={{ bgcolor:'#f0f4ff', border:'1px solid #c5cae9', borderRadius:2, p:1.5, textAlign:'left', width:'100%' }}>
            <Typography variant="caption" color="text.disabled" display="block" mb={0.75} fontWeight={600}>
              {guide.icon} Cách mở lại quyền:
            </Typography>
            {guide.steps.map((s,i) => (
              <Box key={i} sx={{ display:'flex', gap:1, mb:0.5 }}>
                <Typography variant="caption" color="primary.main" sx={{ fontWeight:'bold', minWidth:16 }}>{i+1}.</Typography>
                <Typography variant="caption" color="text.primary">{s}</Typography>
              </Box>
            ))}
          </Box>
        )}
        <Button variant="contained" size="medium" startIcon={<RefreshIcon />}
          onClick={handleRetryCamera} fullWidth
          sx={{ borderRadius:2, fontWeight:'bold', py:1.25, mt:0.5 }}>
          Thử lại — Cấp quyền Camera
        </Button>
      </Box>
    );
  };

  // ── Derived UI values ────────────────────────────────────────────────────────
  const faceStatusCfg  = FACE_STATUS_CONFIG[faceStatus] || FACE_STATUS_CONFIG[FACE_STATUS.LOADING];
  const livenessCfg    = LIVENESS_CONFIG[liveness]      || LIVENESS_CONFIG[LIVENESS.IDLE];
  const showOverlayChips = !imageSrc && !cameraError && !aiLoadingError && faceDetector;
  const isFullyVerified  = faceStatus === FACE_STATUS.OK && liveness === LIVENESS.LIVE;

  // Nút "Xác nhận chấm công" tự đổi màu/label theo trạng thái xác thực hiện tại
  const confirmLabel = isSubmitting
    ? 'ĐANG XỬ LÝ…'
    : isFullyVerified
    ? 'XÁC NHẬN CHẤM CÔNG'
    : faceStatus === FACE_STATUS.MULTI_FACE
    ? 'NHIỀU KHUÔN MẶT — VẪN CHẤM CÔNG?'
    : faceStatus !== FACE_STATUS.OK
    ? 'KHÔNG THẤY MẶT — VẪN CHẤM CÔNG?'
    : liveness === LIVENESS.WATCHING
    ? '👁 ĐANG CHỜ CHỚP MẮT…'
    : 'CHẤM CÔNG (CHƯA XÁC THỰC)';

  const confirmDisabled = !!cameraError || isSubmitting;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={handleClose} fullScreen={isMobile} fullWidth maxWidth="sm"
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{ sx: { bgcolor:'#f4f6f8', ...(isMobile ? {} : { borderRadius:3 }) } }}>

      <DialogTitle sx={{ bgcolor:'primary.main', color:'white', display:'flex', alignItems:'center', justifyContent:'space-between', p:2, pt: isMobile ? 'env(safe-area-inset-top)' : undefined }}>
        <Typography variant="h6" fontWeight="bold">Chấm Công Định Vị AI</Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting} sx={{ color:'white', p:0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p:2, display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' }}>

        {/* ── CAMERA BLOCK ── */}
        <Box sx={{ flexShrink:0, mb:2 }}>
          <Paper elevation={0} sx={{ borderRadius:3, border: cameraError ? '1px solid #ffcdd2' : '1px solid #e0e0e0', overflow:'hidden' }}>

            {/* Viewport (hidden when camera error) */}
            {!cameraError && (
              <Box sx={{
                position:'relative', width:'100%', aspectRatio:'1/1',
                maxHeight: isMobile ? '38vh' : 280,
                bgcolor:'#111', overflow:'hidden',
                display:'flex', justifyContent:'center', alignItems:'center',
              }}>
                {!imageSrc ? (
                  <>
                    <Webcam key={webcamKey} audio={false} ref={webcamRef} 
                      screenshotFormat="image/jpeg"     // Ép định dạng JPEG để tối ưu dung lượng
                      screenshotQuality={0.4}
                      videoConstraints={videoConstraints} onUserMediaError={handleCameraError}
                      style={{ width:'100%', height:'100%', objectFit:'cover', transform:'scaleX(-1)', opacity: faceDetector ? 1 : 0.4, display:'block' }} />
                    {!aiLoadingError && (
                      <canvas ref={canvasRef} style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }} />
                    )}
                    {aiLoadingError && (
                      <Box sx={{ position:'absolute', bottom:8, left:8, right:8, bgcolor:'rgba(106,27,154,0.9)', color:'#fff', borderRadius:1.5, px:1.25, py:0.6, display:'flex', alignItems:'center', gap:0.75 }}>
                        <WarningAmberIcon sx={{ fontSize:16, flexShrink:0 }} />
                        <Typography variant="caption" sx={{ lineHeight:1.3 }}>AI không khả dụng — vẫn có thể chụp ảnh chấm công</Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <img src={imageSrc} alt="Selfie" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                )}

                {/* Manual review banner — shown when AI could not verify the captured photo */}
                {imageSrc && needsManualReview && (
                  <Box sx={{
                    position:'absolute', top:8, left:8, right:8, zIndex:5,
                    display:'flex', alignItems:'center', gap:0.75,
                    bgcolor:'rgba(245,124,0,0.92)', color:'#fff',
                    borderRadius:1.5, px:1.25, py:0.6,
                    boxShadow:'0 2px 8px rgba(0,0,0,0.3)',
                  }}>
                    <WarningAmberIcon sx={{ fontSize:18, flexShrink:0 }} />
                    <Typography variant="caption" fontWeight={700} sx={{ lineHeight:1.3 }}>
                      Chưa xác thực được khuôn mặt — sẽ được hậu kiểm bởi nhân sự
                    </Typography>
                  </Box>
                )}

                {/* AI loading spinner */}
                {!faceDetector && !aiLoadingError && !imageSrc && (
                  <Box sx={{ position:'absolute', color:'white', display:'flex', flexDirection:'column', alignItems:'center', gap:1 }}>
                    <CircularProgress color="inherit" size={28} />
                    <Typography variant="caption">Đang nạp AI model…</Typography>
                  </Box>
                )}

                {/* Overlay chips: face status + liveness */}
                {showOverlayChips && (
                  <Box sx={{ position:'absolute', top:8, left:8, zIndex:5, display:'flex', flexDirection:'column', gap:0.75 }}>
                    {/* Face status chip */}
                    <Chip size="small"
                      icon={faceStatus === FACE_STATUS.OK
                        ? <FaceIcon style={{ color:'#fff', fontSize:14 }} />
                        : <WarningAmberIcon style={{ color:'#fff', fontSize:14 }} />}
                      label={faceStatusCfg.label}
                      sx={{ bgcolor: faceStatusCfg.color, color:'#fff', fontWeight:700, fontSize:'0.67rem',
                        backdropFilter:'blur(4px)', boxShadow:`0 0 8px ${faceStatusCfg.color}88`, transition:'all 0.25s' }} />

                    {/* Liveness chip — only shown when face detected */}
                    {(liveness === LIVENESS.WATCHING || liveness === LIVENESS.LIVE) && (
                      <Chip size="small"
                        icon={<VisibilityIcon style={{ color:'#fff', fontSize:14 }} />}
                        label={liveness === LIVENESS.LIVE ? '✓ Sống động' : '👁 Chớp mắt…'}
                        sx={{
                          bgcolor: liveness === LIVENESS.LIVE ? '#2e7d32' : '#f57c00',
                          color:'#fff', fontWeight:700, fontSize:'0.67rem',
                          backdropFilter:'blur(4px)',
                          boxShadow: liveness === LIVENESS.LIVE ? '0 0 8px #66bb6a88' : '0 0 8px #ffa72688',
                          transition:'all 0.3s',
                          animation: liveness === LIVENESS.WATCHING ? 'livePulse 1.5s ease-in-out infinite' : 'none',
                          '@keyframes livePulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.65 } },
                        }} />
                    )}
                  </Box>
                )}

                {/* Submit overlay */}
                {isSubmitting && (
                  <Box sx={{ position:'absolute', inset:0, bgcolor:'rgba(0,0,0,0.72)', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', color:'white', zIndex:10 }}>
                    <CircularProgress color="inherit" sx={{ mb:2 }} />
                    <Typography variant="body2" fontWeight="bold">{loadingStep}</Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Camera error: icon strip (dark) + details + retry (light, no height cap) */}
            {cameraError && (
              <>
                <Box sx={{ bgcolor:'#1a1a1a', display:'flex', justifyContent:'center' }}>
                  <CameraErrorIconStrip />
                </Box>
                <CameraErrorDetails />
              </>
            )}

            {/* HUD debug bar */}
            {!cameraError && (
              <HudBar
                faceStatus={faceStatus} faceDetector={faceDetector}
                aiLoadingError={aiLoadingError} imageSrc={imageSrc}
                liveness={liveness}
              />
            )}

          </Paper>
        </Box>

        {/* ── FORM BLOCK ── */}
        <Box sx={{ flexGrow:1, overflowY:'auto', minHeight:0, pr:0.5,
          '&::-webkit-scrollbar':{ width:'4px' },
          '&::-webkit-scrollbar-thumb':{ backgroundColor:'rgba(0,0,0,0.2)', borderRadius:'4px' } }}>
          <Paper elevation={0} sx={{ p:2, borderRadius:3, border:'1px solid #e0e0e0', display:'flex', flexDirection:'column', gap:2 }}>
            <Box sx={{ display:'flex', alignItems:'center', color:'text.secondary', mb:-1 }}>
              <MyLocationIcon fontSize="small" sx={{ mr:1 }} />
              <Typography variant="caption">Tự động đính kèm toạ độ GPS</Typography>
            </Box>
            <Divider />
            <LookupFieldControl field={customerFieldConfig} value={customerId} onChange={(val) => setCustomerId(val)} isReadOnly={isSubmitting} />
            <TextField label="Ghi chú công việc" multiline rows={2} fullWidth variant="outlined"
              value={note} onChange={(e) => setNote(e.target.value)} disabled={isSubmitting} />
          </Paper>
        </Box>

        {/* ── CONFIRM (tự chụp ảnh + chấm công trong 1 lần nhấn) ── */}
        <Box sx={{ flexShrink:0, mt:2, pb: isMobile ? 2 : 0 }}>
          <Button fullWidth variant="contained"
            color={isFullyVerified || isSubmitting ? 'success' : 'warning'}
            size="large"
            startIcon={!isSubmitting && (isFullyVerified ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />)}
            onClick={handleCheckIn} disabled={confirmDisabled}
            sx={{ py:1.75, borderRadius:3, fontWeight:'bold', fontSize:'1rem', transition:'all 0.25s' }}>
            {confirmLabel}
          </Button>
        </Box>

      </DialogContent>
    </Dialog>
  );
}